import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LinearProgress from '@mui/material/LinearProgress';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Typography from '@mui/material/Typography';
import React from 'react';
import { useUpdateChargePoint } from '../../../app/ApiGen';
import { Dialog } from '../../../components/Dialog';
import type { TableChargePoint } from './utils';

type FlexSolarMode = 'unlock' | 'lock';

type FlexUpgradeDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  chargePoints: TableChargePoint[];
};

enum UpgradeStatus {
  PENDING = 'Pending',
  LOADING = 'Loading',
  SUCCESS = 'Success',
  ERROR = 'Error',
}

type ItemResult = { id: string; status: UpgradeStatus; error?: string };

const StatusIcon: React.FC<{ result: ItemResult }> = ({ result }) => {
  switch (result.status) {
    case UpgradeStatus.LOADING:
      return <CircularProgress size={20} />;
    case UpgradeStatus.SUCCESS:
      return <CheckCircleOutlineIcon color="success" />;
    case UpgradeStatus.ERROR:
      return (
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="caption" color="error">
            {result.error}
          </Typography>
          <ErrorOutlineIcon color="error" />
        </Box>
      );
    default:
      return (
        <Typography variant="caption" color="textSecondary">
          Queued
        </Typography>
      );
  }
};

const SequentialExecutor: React.FC<{ chargePoints: TableChargePoint[]; mode: FlexSolarMode }> = ({
  chargePoints,
  mode,
}) => {
  // Snapshot the list on mount so the effect never replays for rows that were already
  // mutated if the parent re-renders with a new `chargePoints` array reference.
  const [execution] = React.useState(() => chargePoints);

  const [results, setResults] = React.useState<Map<string, ItemResult>>(() => {
    const entries: [string, ItemResult][] = execution.map((cp) => [
      cp.id,
      { id: cp.id, status: UpgradeStatus.PENDING },
    ]);
    return new Map(entries);
  });
  const [currentIndex, setCurrentIndex] = React.useState(0);

  const currentCp = execution[currentIndex];
  const { mutate } = useUpdateChargePoint({ id: currentCp?.id ?? '' });

  const completedCount = Array.from(results.values()).filter(
    (r) => r.status === UpgradeStatus.SUCCESS || r.status === UpgradeStatus.ERROR,
  ).length;

  React.useEffect(() => {
    if (currentIndex >= execution.length) return;

    const cp = execution[currentIndex];

    setResults((prev) => {
      const next = new Map(prev);
      next.set(cp.id, { id: cp.id, status: UpgradeStatus.LOADING });
      return next;
    });

    mutate({
      data: {
        attributes: { features: { Solar: { unlocked: mode === 'unlock' } } },
        id: cp.id,
        type: 'chargePoints',
      },
    })
      .then(() => {
        setResults((prev) => {
          const next = new Map(prev);
          next.set(cp.id, { id: cp.id, status: UpgradeStatus.SUCCESS });
          return next;
        });
        setCurrentIndex((i) => i + 1);
      })
      .catch((err) => {
        setResults((prev) => {
          const next = new Map(prev);
          next.set(cp.id, {
            id: cp.id,
            status: UpgradeStatus.ERROR,
            error: err instanceof Error ? err.message : 'Failed',
          });
          return next;
        });
        setCurrentIndex((i) => i + 1);
      });
    // Only re-run when currentIndex changes, not when mutate reference changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, execution, mode]);

  const progress = execution.length > 0 ? (completedCount / execution.length) * 100 : 0;

  return (
    <>
      <Box sx={{ mb: 1 }}>
        <LinearProgress variant="determinate" value={progress} />
        <Typography variant="caption" color="textSecondary">
          {completedCount} / {execution.length} completed
        </Typography>
      </Box>
      <List dense sx={{ maxHeight: 400, overflow: 'auto' }}>
        {execution.map((cp) => {
          const result = results.get(cp.id) ?? { id: cp.id, status: UpgradeStatus.PENDING };
          return (
            <ListItem key={cp.id}>
              <ListItemText
                primary={cp.name || cp.ocppChargePointId}
                secondary={`${cp.model ?? 'Unknown model'} - ${cp.serial ?? 'No serial'}`}
              />
              <StatusIcon result={result} />
            </ListItem>
          );
        })}
      </List>
    </>
  );
};

const actionLabel = (mode: FlexSolarMode) => (mode === 'unlock' ? 'Unlock' : 'Lock');

export const FlexUpgradeDialog: React.FC<FlexUpgradeDialogProps> = ({ isOpen, onClose, chargePoints }) => {
  const [executing, setExecuting] = React.useState(false);
  const [mode, setMode] = React.useState<FlexSolarMode>('unlock');

  const flexChargePoints = chargePoints.filter((cp) => cp.model?.startsWith('E2F'));
  const nonFlexCount = chargePoints.length - flexChargePoints.length;
  const count = flexChargePoints.length;
  const plural = count !== 1;

  const handleClose = React.useCallback(() => {
    setExecuting(false);
    setMode('unlock');
    onClose();
  }, [onClose]);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      title={`Flex Solar - ${actionLabel(mode)}`}
      maxWidth="sm"
      submitButton={
        // Cancel rendered alongside the primary (not via `hasCancelButton`) so the shared
        // Dialog's `space-between` puts Cancel on the left and the primary on the right —
        // matching the Save-on-the-right convention used elsewhere, without changing the
        // default order for other callers of the Dialog component.
        <>
          <Button color="inherit" onClick={handleClose} variant="contained">
            Cancel
          </Button>
          {!executing ? (
            <Button color="primary" variant="contained" onClick={() => setExecuting(true)} disabled={count === 0}>
              {actionLabel(mode)} Solar on {count} charge point{plural ? 's' : ''}
            </Button>
          ) : (
            <Button color="primary" variant="contained" onClick={handleClose}>
              Done
            </Button>
          )}
        </>
      }
    >
      {!executing && (
        <Box mb={2}>
          <RadioGroup row value={mode} onChange={(e) => setMode(e.target.value as FlexSolarMode)}>
            <FormControlLabel value="unlock" control={<Radio size="small" />} label="Unlock Solar" />
            <FormControlLabel value="lock" control={<Radio size="small" />} label="Lock Solar" />
          </RadioGroup>
        </Box>
      )}
      {nonFlexCount > 0 && (
        <Typography variant="body2" color="warning.main" gutterBottom>
          {nonFlexCount} selected charge point{nonFlexCount !== 1 ? 's are' : ' is'} not a flex model (E2F) and will be
          skipped.
        </Typography>
      )}
      {count === 0 && <Typography variant="body2">No flex (E2F) charge points in selection.</Typography>}
      {count > 0 && !executing && (
        <Typography variant="body2">
          This will {mode} the Solar feature on {count} flex charge point{plural ? 's' : ''} sequentially. Confirm to
          proceed.
        </Typography>
      )}
      {count > 0 && executing && <SequentialExecutor chargePoints={flexChargePoints} mode={mode} />}
    </Dialog>
  );
};
